package com.anjuros.website.redesign.sections.blogarticle

import androidx.compose.runtime.Composable
import com.anjuros.website.components.language.LocalText
import com.anjuros.website.redesign.components.RdPrimaryButton
import com.anjuros.website.redesign.components.RdRichText
import com.anjuros.website.redesign.components.RdScrollTable
import com.anjuros.website.redesign.components.RdTag
import com.anjuros.website.redesign.components.isOnlyLink
import com.anjuros.website.redesign.sections.blog.RdBlogGuide
import com.anjuros.website.redesign.styles.ArticleDevicesStyle
import com.anjuros.website.redesign.styles.ArticleIntroStyle
import com.anjuros.website.redesign.styles.ArticleMoreLinkStyle
import com.anjuros.website.redesign.styles.ArticleNumberStyle
import com.anjuros.website.redesign.styles.ArticleNumberedHeadingStyle
import com.anjuros.website.redesign.styles.ArticlePhotoStyle
import com.anjuros.website.redesign.styles.ArticleProseStyle
import com.anjuros.website.redesign.styles.ArticleQuestionsStyle
import com.anjuros.website.redesign.styles.ArticleTableFitStyle
import com.anjuros.website.redesign.styles.ArticleTableStyle
import com.anjuros.website.redesign.styles.ArticleTableWrapStyle
import com.anjuros.website.redesign.styles.ArticleWideTableStyle
import com.anjuros.website.redesign.styles.ArticleWrapUpStyle
import com.anjuros.website.redesign.util.RdRoute
import com.anjuros.website.redesign.util.RdText
import com.anjuros.website.templates.selection.modules.BlogGraphic
import com.anjuros.website.templates.selection.modules.BlogParagraph
import com.anjuros.website.templates.selection.modules.BlogSection
import com.anjuros.website.templates.selection.modules.BlogTableColumn
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Table
import org.jetbrains.compose.web.dom.Tbody
import org.jetbrains.compose.web.dom.Td
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Th
import org.jetbrains.compose.web.dom.Thead
import org.jetbrains.compose.web.dom.Tr
import org.jetbrains.compose.web.dom.Ul

/** "1. A clear headline" → number "1" and heading "A clear headline". */
private val NumberedTitle = Regex("""^(\d+)\.\s+(.+)$""")

/** An anchor for a section, made from its heading, such as "a-clear-headline". */
fun articleSectionId(title: String): String =
    title.lowercase().replace(Regex("[^a-z0-9]+"), "-").trim('-')

/** The whole article text, one section after another, read from the article's [com.anjuros.website.templates.selection.modules.BlogCard]. */
@Composable
fun ArticleContent(guide: RdBlogGuide) {
    RdTag("article", ArticleProseStyle.toModifier().attrsModifier { attr("aria-labelledby", "h1") }) {
        guide.card.blogSectionList.forEachIndexed { index, section ->
            ArticleSection(section, isIntro = index == 0, isWrapUp = section.sectionTitle == guide.wrapUp)
        }
    }
}

@Composable
private fun ArticleSection(section: BlogSection, isIntro: Boolean, isWrapUp: Boolean) {
    val title = LocalText(section.sectionTitle)
    val numbered = NumberedTitle.find(title)
    val modifier = Modifier.id(articleSectionId(title))
        .thenIf(isIntro, ArticleIntroStyle.toModifier())
        .thenIf(isWrapUp, ArticleWrapUpStyle.toModifier())
    RdTag("section", modifier) {
        H2((if (numbered != null) ArticleNumberedHeadingStyle.toModifier() else Modifier).toAttrs()) {
            if (numbered != null) {
                Span(ArticleNumberStyle.toModifier().toAttrs()) { Text(numbered.groupValues[1]) }
                // Its own box, so a second line starts in line with the first rather than under the number.
                Span { Text(numbered.groupValues[2]) }
            } else {
                Text(title)
            }
        }
        // Paragraphs before the first question are plain text; each question then gets a card with its answer.
        val firstQuestion = section.paragraphList.indexOfFirst { it.isSubtitle }
        val plain = if (firstQuestion < 0) section.paragraphList else section.paragraphList.take(firstQuestion)
        plain.forEach { ArticleParagraph(it) }
        if (firstQuestion >= 0) Questions(section.paragraphList.drop(firstQuestion))
        if (isWrapUp) RdPrimaryButton(RdRoute.CONTACT, RdText.BLOG_GET_IN_TOUCH, withArrow = true)
    }
}

@Composable
private fun Questions(paragraphs: List<BlogParagraph>) {
    val groups = mutableListOf<MutableList<BlogParagraph>>()
    paragraphs.forEach { if (it.isSubtitle || groups.isEmpty()) groups.add(mutableListOf(it)) else groups.last().add(it) }
    Div(ArticleQuestionsStyle.toModifier().toAttrs()) {
        groups.forEach { group ->
            Div {
                H3 { RdRichText(LocalText(group.first().paragraphText)) }
                group.drop(1).forEach { ArticleParagraph(it) }
            }
        }
    }
}

@Composable
private fun ArticleParagraph(paragraph: BlogParagraph) {
    val text = paragraph.paragraphText?.let { LocalText(it) }.orEmpty()
    if (text.isNotBlank()) {
        val style = if (isOnlyLink(text)) ArticleMoreLinkStyle.toModifier() else Modifier
        P(style.toAttrs()) { RdRichText(text) }
    }
    if (paragraph.listPoints.isNotEmpty()) {
        // One span per point, so text and links stay together in the text column next to the tick.
        Ul { paragraph.listPoints.forEach { point -> Li { Span { RdRichText(LocalText(point)) } } } }
    }
    paragraph.graphic?.takeIf { it.isNotEmpty() }?.let { Graphics(it) }
    if (paragraph.blogTableColumn.isNotEmpty()) ArticleTable(paragraph.blogTableColumn)
}

/** Photos are shown as they are; screenshots on devices (no shadow) sit side by side on a pale-blue panel. */
@Composable
private fun Graphics(graphics: List<BlogGraphic>) {
    val onDevices = graphics.none { it.showShadow }
    RdTag("figure", if (onDevices) ArticleDevicesStyle.toModifier() else ArticlePhotoStyle.toModifier()) {
        graphics.forEach { graphic ->
            Img(src = graphic.graphicImage, alt = graphic.graphicAlt) { attr("loading", "lazy") }
        }
    }
}

@Composable
private fun ArticleTable(columns: List<BlogTableColumn>) {
    val rows = columns.firstOrNull()?.columnPoints?.size ?: 0
    val tableModifier = ArticleTableStyle.toModifier().thenIf(columns.size > 2, ArticleWideTableStyle.toModifier())
    RdScrollTable(ArticleTableWrapStyle.toModifier(), radiusPx = 14, ArticleTableFitStyle.toModifier()) {
        Table(tableModifier.toAttrs()) {
            Thead { Tr { columns.forEach { Th { Text(LocalText(it.columnTitle)) } } } }
            Tbody {
                repeat(rows) { row ->
                    Tr { columns.forEach { column -> Td { RdRichText(LocalText(column.columnPoints[row])) } } }
                }
            }
        }
    }
}
